'use strict';

const {
	section, h2, a, div, iframe, img
} = require('iblokz-snabbdom-helpers');

const copyable = require('../comp/copyable');
const md = require('../../util/md');
const _rl = require('../../util/resolve-localized');
const {resolveLocalized, DEFAULT_LOCALE} = _rl.default || _rl;

const t = (i18n, key, fallback) =>
	(i18n && i18n.widget && i18n.widget.addressMap && i18n.widget.addressMap[key]) || fallback;

const actionLabel = (i18n, key, fallback) =>
	(i18n && i18n.common && i18n.common.actions && i18n.common.actions[key]) || fallback;

const hasGeo = address => {
	const geo = address && address.geo;
	if (!geo || geo.lat == null || geo.lng == null) return false;
	const lat = Number(geo.lat);
	const lng = Number(geo.lng);
	if (Number.isNaN(lat) || Number.isNaN(lng)) return false;
	// (0, 0) is an empty sentinel from forms / unset venue overrides
	if (lat === 0 && lng === 0) return false;
	return true;
};

const formatAddressLine = address => {
	if (!address) return '';
	const cityZip = [address.city, address.zip].filter(Boolean).join(' ').trim();
	return [address.street, cityZip, address.country].filter(Boolean).join(', ');
};

const addressHasContent = address => {
	if (!address) return false;
	return Boolean(
		address.street || address.city || address.zip || address.country
		|| (address.geo && (address.geo.lat != null || address.geo.lng != null))
	);
};

const osmEmbedSrc = (lat, lng, delta = 0.004) => {
	const la = Number(lat);
	const lo = Number(lng);
	const bbox = [lo - delta, la - delta, lo + delta, la + delta].join('%2C');
	return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${encodeURIComponent(la)}%2C${encodeURIComponent(lo)}`;
};

const osmLink = (lat, lng) =>
	`https://www.openstreetmap.org/?mlat=${encodeURIComponent(lat)}&mlon=${encodeURIComponent(lng)}#map=17/${encodeURIComponent(lat)}/${encodeURIComponent(lng)}`;

const osmSearchLink = addressLine =>
	`https://www.openstreetmap.org/search?query=${encodeURIComponent(addressLine)}`;

const titleText = (title, i18n, lang) => {
	const resolved = resolveLocalized(title, lang || DEFAULT_LOCALE);
	return resolved || t(i18n, 'title', 'Address');
};

const isExternalUrl = url => /^https?:\/\//i.test(String(url || ''));

const linkAttrs = url => {
	const attrs = {href: url};
	if (isExternalUrl(url)) {
		attrs.target = '_blank';
		attrs.rel = 'noopener';
	}
	return attrs;
};

const avatarEl = avatar => img('.widget-address-map-avatar', {
	attrs: {src: avatar, alt: ''}
});

const headingEl = ({title, url, i18n, lang}) => {
	const label = titleText(title, i18n, lang);
	return url
		? a('.widget-address-map-title', {attrs: linkAttrs(url)}, label)
		: h2('.widget-address-map-title', label);
};

/**
 * Lightweight venue address + OSM embed (no JS map library).
 * Optional `avatar`, `url`, and `description` enrich the header (e.g. linked venue profile).
 * Renders only when address has content.
 */
const addressMap = ({address, title, i18n, avatar, url, description, lang}) => {
	if (!addressHasContent(address)) return '';

	const locale = lang || DEFAULT_LOCALE;
	const line = formatAddressLine(address);
	const geoOk = hasGeo(address);
	const lat = geoOk ? address.geo.lat : null;
	const lng = geoOk ? address.geo.lng : null;
	const mapHref = geoOk ? osmLink(lat, lng) : (line ? osmSearchLink(line) : null);
	const enriched = Boolean(avatar || url || description);
	const heading = titleText(title, i18n, locale);

	const header = enriched
		? [
			div('.widget-address-map-header', [
				avatar
					? (url
						? a('.widget-address-map-avatar-link', {
							attrs: Object.assign(linkAttrs(url), {title: heading})
						}, [avatarEl(avatar)])
						: avatarEl(avatar))
					: '',
				div('.widget-address-map-heading', [
					headingEl({title, url, i18n, lang: locale})
				])
			]),
			description
				? div('.widget-address-map-desc', {
					props: {innerHTML: md.toDisplayHTML(description)}
				})
				: ''
		]
		: h2(heading);

	return section('.widget-address-map', [
		...(Array.isArray(header) ? header : [header]),
		line
			? copyable({
				text: line,
				title: actionLabel(i18n, 'copy', 'Copy'),
				copiedLabel: actionLabel(i18n, 'copied', 'Copied')
			})
			: '',
		geoOk
			? div('.widget-address-map-frame', [
				iframe({
					attrs: {
						src: osmEmbedSrc(lat, lng),
						title: t(i18n, 'mapTitle', 'Map'),
						loading: 'lazy',
						referrerpolicy: 'no-referrer-when-downgrade'
					}
				})
			])
			: '',
		mapHref
			? a('.widget-address-map-open[target="_blank"][rel="noopener"]', {
				attrs: {href: mapHref}
			}, t(i18n, 'openOsm', 'Open in OpenStreetMap'))
			: ''
	]);
};

module.exports = addressMap;
module.exports.addressHasContent = addressHasContent;
module.exports.formatAddressLine = formatAddressLine;
module.exports.hasGeo = hasGeo;
